import { MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InfoIconDto,
  TableauChartDto,
} from '@asset-metrix/portal-gateway-client-js';
import { WidgetProps } from '@asset-metrix/portal-ui';

import { useFilterSelections } from '../../store/hooks/useFilterSelections';
import { widgetShareAction } from '../table/widgetShareAction';

declare global {
  interface Window {
    tableau: {
      Viz: new (...args: unknown[]) => TableauViz;
    };
  }
}

export type TableauViz = {
  dispose: () => void;
  showExportImageDialog: () => void;
};

export type BaseUseTableauChartActionProps = {
  infoIcon?: InfoIconDto;
  pageFilterInstanceId: string;
  title: string;
  widgetId: string;
  closeFullscreen?: () => void;
  openInfoModal?: () => void;
  openFullscreen?: () => void;
};

export const useTableauChartActions = ({
  config,
  infoIcon,
  pageFilterInstanceId,
  tableauInstance,
  title,
  widgetId,
  closeFullscreen,
  openInfoModal,
  openFullscreen,
}: BaseUseTableauChartActionProps & {
  config: TableauChartDto['config'];
  tableauInstance: MutableRefObject<Promise<TableauViz> | undefined>;
}) => {
  const { t } = useTranslation();
  const actions: WidgetProps['actions'] = [];

  const downloadAsImage = (
    tableauInstanceForDl: MutableRefObject<Promise<TableauViz> | undefined>
  ) => {
    tableauInstanceForDl.current
      ?.then((viz) => viz.showExportImageDialog())
      .catch((err) => err);
  };

  const pageFilterSelections = useFilterSelections(pageFilterInstanceId);

  const shareActions =
    config.shareActions
      ?.filter((action) => action.actionKey !== 'imageDownload')
      .map((sa) =>
        widgetShareAction(
          widgetId,
          title || `chart-${config.tableauWorksheet}` || `chart-${widgetId}`,
          sa,
          t,
          {
            pageFilterSelections,
          }
        )
      ) ?? [];

  if (config.shareActions?.find((sa) => sa.actionKey === 'imageDownload')) {
    shareActions.push({
      label: t('app.widget.action.label.imageDownload'),
      cb: () => downloadAsImage(tableauInstance),
    });
  }
  if (shareActions.length > 0) {
    actions.push({
      icon: { name: 'share' },
      title: t('app.widget.action.label.shareActions'),
      moreActions: shareActions,
    });
  }

  if (infoIcon != null) {
    actions.push({
      icon: { name: 'info' },
      title: t('app.widget.action.label.additionalInformation'),
      cb: openInfoModal,
    });
  }

  if (openFullscreen) {
    actions.push({
      icon: { name: 'open_in_full' },
      title: t('app.widget.action.label.openInFullscreen'),
      cb: openFullscreen,
    });
  }
  if (closeFullscreen) {
    actions.push({
      icon: { name: 'close' },
      title: t('app.widget.action.label.closeFullscreen'),
      cb: closeFullscreen,
    });
  }

  return actions;
};
